import { callAPI } from "./apiUtils";
import { cleanChatMessageText } from "./emailBodyFormat";
import {
  BOOKMARK_TODO_CREATED,
  BOOKMARK_TODO_FAILED,
  BOOKMARK_TODO_PENDING,
  TODO_ITEM_REMOVED,
} from "../store";

const inFlight = new Set();

function asId(value) {
  if (value == null || value === "") return null;
  if (typeof value === "object") return value.id || value._id || null;
  return value;
}

/**
 * The project an email belongs to (its first linked project, else its room
 * fields), or null. Only an email with a project can become a task.
 */
export function emailProjectIdOf(email) {
  if (!email) return null;
  const linked = email.linkedProjects || email.linkedProject;
  const first = Array.isArray(linked) ? linked[0] : linked;
  const fromLink = first && typeof first === "object" ? first.id || first.buildingId || first._id : first;
  return asId(fromLink) || asId(email.buildingId) || asId(email.building) || asId(email.roomId) || null;
}

export function isChatBookmarked(bookmarkedChatIds, chat) {
  const id = String(chat?.id || chat?._id || "");
  if (!id) return false;
  if (chat?.isTodoBookmarked) return true;
  return (bookmarkedChatIds || []).some((x) => String(x) === id);
}

export function canCopyChatMessage(chat) {
  const type = String(chat?.messageType || "").toLowerCase();
  if (type === "audio") return !!String(chat?.transcribedAudio || "").trim();
  if (type === "image" || type === "doc" || type === "video" || type === "map" || type === "poll" || type === "contact") {
    return false;
  }
  return true;
}

export function canBookmarkChatMessage(chat) {
  if (!chat || chat.isDeleted) return false;
  const type = String(chat?.messageType || "").toLowerCase();
  if (type === "audio") return !!String(chat?.transcribedAudio || "").trim();
  if (type === "image" || type === "doc" || type === "simple") return true;
  return false;
}

function creatingCopy(strings) {
  return (
    (strings && (strings.bookmark_todo_creating || strings.creating_todo)) ||
    "Bookmarked task is being created..."
  );
}

export function beginBookmarkTodo(dispatch, payload) {
  dispatch({
    type: BOOKMARK_TODO_PENDING,
    tempId: payload.tempId,
    chatId: payload.chatId || null,
    groupId: payload.groupId || null,
    emailId: payload.emailId || null,
    roomId: asId(payload.roomId),
    description: payload.description,
  });
}

export function finishBookmarkTodo(dispatch, payload = {}) {
  dispatch({
    type: BOOKMARK_TODO_CREATED,
    tempId: payload.tempId || null,
    chatId: payload.chatId || null,
    groupId: payload.groupId || null,
    roomId: asId(payload.roomId),
    todo: payload.todo || null,
  });
}

export function failBookmarkTodo(dispatch, payload = {}) {
  dispatch({
    type: BOOKMARK_TODO_FAILED,
    tempId: payload.tempId || null,
    chatId: payload.chatId || null,
  });
}

function decryptChatText(chat, secretKey, decryptMessage) {
  if (chat?.messageType === "audio" && chat?.transcribedAudio) {
    return chat.transcribedAudio;
  }
  if (secretKey && typeof decryptMessage === "function" && chat?.message) {
    try {
      return decryptMessage(chat.message, secretKey) || chat.message || "";
    } catch (_) {
      return chat.message || "";
    }
  }
  return chat?.message || "";
}

export async function toggleChatBookmarkTodo({
  dispatch,
  chat,
  groupId,
  roomId,
  secretKey,
  decryptMessage,
  strings,
  isBookmarked,
}) {
  const chatId = String(chat?.id || chat?._id || "");
  if (!chatId) return { error: "missing chat" };
  const lockKey = String(chatId);
  if (inFlight.has(lockKey)) return { busy: true };
  inFlight.add(lockKey);

  try {
    if (isBookmarked) {
      dispatch({
        type: TODO_ITEM_REMOVED,
        groupId: groupId || null,
        chatId,
      });
      try {
        const removeRes = await callAPI("/todo/removeBookmark", {}, "POST", { chatId, groupId });
        if (removeRes?.data?.status && removeRes.data.kept) {
          dispatch({ type: BOOKMARK_TODO_CREATED, chatId });
          return { kept: true };
        }
        if (removeRes?.data?.status && removeRes.data.removed) {
          return { removed: true };
        }
      } catch (_) { /* already dropped locally */ }
      return { removed: true };
    }

    const cleanText = cleanChatMessageText(decryptChatText(chat, secretKey, decryptMessage));
    const tempId = `pending-bookmark-${chatId}-${Date.now()}`;
    const resolvedRoomId = asId(roomId);
    beginBookmarkTodo(dispatch, {
      tempId,
      chatId,
      groupId,
      roomId: resolvedRoomId,
      description: creatingCopy(strings),
    });

    try {
      const payload = { messageText: cleanText };
      if (groupId) payload.group = groupId;
      else payload.group = chatId;
      payload.chat = chatId;
      if (resolvedRoomId) payload.roomId = resolvedRoomId;

      const { data } = await callAPI("/todo/manualTodoCreate", {}, "POST", payload);
      const rawTodo = data?.data;
      const created = rawTodo && (rawTodo.id || rawTodo.todoId)
        ? rawTodo
        : {
            id: tempId,
            todoId: tempId,
            description: creatingCopy(strings),
            chat: chatId,
            group: groupId || null,
            buildingList: resolvedRoomId,
            isBookmarked: true,
            isCreating: true,
            createdAt: new Date().toISOString(),
          };
      if (data?.status) {
        finishBookmarkTodo(dispatch, { tempId, chatId, groupId, roomId: resolvedRoomId, todo: created });
        return { created: true, todo: created };
      }
      failBookmarkTodo(dispatch, { tempId, chatId });
      return { error: data?.message || "Failed to create ToDo" };
    } catch (createErr) {
      failBookmarkTodo(dispatch, { tempId, chatId });
      const apiMessage =
        (typeof createErr === "string" && createErr) ||
        createErr?.response?.data?.message ||
        (typeof createErr?.message === "string" && !/^request failed/i.test(createErr.message) && createErr.message) ||
        null;
      return { error: apiMessage || "Failed to create ToDo" };
    }
  } catch (error) {
    return { error };
  } finally {
    inFlight.delete(lockKey);
  }
}

export function beginExternalBookmarkTodo(dispatch, { tempId, chatId, groupId, emailId, roomId, strings }) {
  beginBookmarkTodo(dispatch, {
    tempId,
    chatId,
    groupId,
    emailId,
    roomId: asId(roomId),
    description: creatingCopy(strings),
  });
}
